import { Boolean, Dictionary, Literal, Number, Optional, Record, Static, String } from 'runtypes';

const ThemeSchemeRunType = Record({
	dark: String,
	neon: Boolean,
	primary: String,
	secondary: String,
	uniColor: String,
});

export const ThemeRunType = Record({
	deletable: Boolean,
	label: String,
	value: ThemeSchemeRunType,
});

export const ThemeSchemeQueryRunType = Record({
	scheme: String,
});

export type ThemeSchemeQuery = Static<typeof ThemeSchemeQueryRunType>;

export const SelectedColorSchemeForTheInstanceRunType = Record({
	instance: String,
	scheme: String,
});

export type SelectedColorSchemeForTheInstance = Static<typeof SelectedColorSchemeForTheInstanceRunType>;

export type Theme = Static<typeof ThemeRunType>;

export const NotificationConfigurationRunType = Record({
	notificationsAppointmentEnabled: Boolean,
	notificationsDocumentsEnabled: Boolean,
	notificationsIdoEnabled: Optional(Boolean),
	notificationsMailEnabled: Boolean,
	notificationsMessageEnabled: Boolean,
	notificationsNotesEnabled: Boolean,
	notificationsPhoneCallsEnabled: Boolean,
	notificationsTasksEnabled: Boolean,
});

export type NotificationConfiguration = Static<typeof NotificationConfigurationRunType>;

export const IdoNotificationSubscriptionRunType = Record({
	instanceId: String,
	savedByUserId: Number,
	source: Literal('all').Or(Literal('explicit')),
});

export type IdoNotificationSubscription = Static<typeof IdoNotificationSubscriptionRunType>;

export const EditorStandardFontRunType = Record({
	fontFamily: String,
	fontSize: String,
});

export type EditorStandardFont = Static<typeof EditorStandardFontRunType>;

export const UserPreferencesRunType = Dictionary(String, String);

export type UserPreferences = Static<typeof UserPreferencesRunType>;

export const NAVIGATION_MODE_PREFERENCE_KEY = 'NAVIGATION_MODE';
export const TIMER_ENABLED_PREFERENCE_KEY = 'TIMER_ENABLED';
export const WIDGET_SIDEBAR_ENABLED_PREFERENCE_KEY = 'WIDGET_SIDEBAR_ENABLED';
export const WIDGET_SIDEBAR_ACTIVE_WIDGETS_PREFERENCE_KEY = 'WIDGET_SIDEBAR_ACTIVE_WIDGETS';

export const SETUP_WIZARD_COMPLETED_PREFERENCE_KEY = 'SETUP_WIZARD_COMPLETED';
export const THEME_MODE_PREFERENCE_KEY = 'THEME_MODE';
export const THEME_PRIMARY_COLOR_PREFERENCE_KEY = 'THEME_PRIMARY_COLOR';
export const THEME_SURFACE_COLOR_PREFERENCE_KEY = 'THEME_SURFACE_COLOR';
export const MENU_ITEMS_SOLID_PREFERENCE_KEY = 'MENU_ITEMS_SOLID';
export const APP_FONT_SIZE_PT_PREFERENCE_KEY = 'APP_FONT_SIZE_PT';
export const COMPACT_BUTTON_VIEW_PREFERENCE_KEY = 'COMPACT_BUTTON_VIEW';
export const DEFAULT_LIST_PER_MODULE_PREFERENCE_KEY = 'DEFAULT_LIST_PER_MODULE';
export const UNI_COLOR_BUTTONS_PREFERENCE_KEY = 'UNI_COLOR_BUTTONS';
export const SPOTLIGHT_SEARCH_ENABLED_PREFERENCE_KEY = 'SPOTLIGHT_SEARCH_ENABLED';
export const USER_ONLINE_POSITION_PREFERENCE_KEY = 'USER_ONLINE_POSITION';
export const USER_ONLINE_SHOW_OFFLINE_USERS_PREFERENCE_KEY = 'USER_ONLINE_SHOW_OFFLINE_USERS';
export const KANBAN_REMEMBER_LAST_BOARD_PREFERENCE_KEY = 'KANBAN_REMEMBER_LAST_BOARD';
export const IDO_NOTIFICATION_SUBSCRIPTIONS_PREFERENCE_KEY = 'IDO_NOTIFICATION_SUBSCRIPTIONS';

// Arbeitsmodus: geräteübergreifend, damit das Layout nach dem Login auf jedem Gerät gleich aussieht.
// Der Split-View bleibt bewusst gerätelokal — er hängt an Zeigegerät und Viewport, nicht am Konto.
export const WORKSPACE_TABS_ENABLED_PREFERENCE_KEY = 'WORKSPACE_TABS_ENABLED';
export const WORKSPACE_TABS_POSITION_PREFERENCE_KEY = 'WORKSPACE_TABS_POSITION';
export const WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW_PREFERENCE_KEY = 'WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW';
export const WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW_PREFERENCE_KEY = 'WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW';

// Tastenkürzel: geräteübergreifend als ein JSON-Envelope (bindings, enabled, presetName),
// damit ein localStorage-Verlust die Konfiguration nicht zurücksetzt.
export const SHORTCUT_SETTINGS_PREFERENCE_KEY = 'SHORTCUT_SETTINGS';

export enum ConfigurableLayoutSlotButtons {
	AVATAR = 'AVATAR',
	CONTACT_INFORMATION = 'CONTACT_INFORMATION',
	CREATE = 'CREATE',
	EMTPY_SLOT = 'EMTPY_SLOT',
	IDOS = 'IDOS',
	LOADING_INDICATOR = 'LOADING_INDICATOR',
	MENU = 'MENU',
	SEARCH = 'SEARCH',
	SETTINGS = 'SETTINGS',
	TIMER = 'TIMER',
	USER_ONLINE = 'USER_ONLINE',
}

export type MenuOrder = {
	addOns: number;
	agendaList: number;
	createMenu: number;
	documentList: number;
	filterList: number;
	folderExplorer: number;
	inbox: number;
	kanbanBoard: number;
	logout: number;
	mailList: number;
	messageList: number;
	noteList: number;
	phoneCallList: number;
	records: number;
	settings: number;
	taskList: number;
	timer: number;
	trashList: number;
	userOnline: number;
};
